import React from 'react';
import { TrendingUp, DollarSign, Target, Zap, ArrowUpRight, ArrowDownRight, Wallet, Activity } from 'lucide-react';
import { formatNumber } from '../../utils';

const TIME_RANGES = {
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year'
};

export const OverviewTab = ({ stats = {}, timeRange, setTimeRange, weeklyProgress = {}, projectSettings = {}, roiCalculation = {}, isDark, colors }) => {
  const totalVolume = stats.totalVolume || 0;
  const totalCosts = stats.totalCosts || 0;
  const totalPoints = stats.totalPoints || 0;
  const totalFees = stats.totalFees || 0;
  const costPerPoint = totalPoints > 0 ? totalCosts / totalPoints : 0;
  
  const hasData = totalVolume > 0 || totalCosts > 0 || totalPoints > 0 || totalFees > 0;
  const activeProjects = Object.keys(projectSettings).length;
  const projectsWithProgress = Object.keys(weeklyProgress).length;

  // Check if any project uses fees as metric
  const hasFeeProjects = Object.values(projectSettings).some(p => p.metricType === 'fees');

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Time Range Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4">
          <span className={`font-medium text-sm sm:text-base ${colors.textSecondary}`}>Period:</span>
          <div className={`flex gap-1 p-1 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-200'}`}>
            {Object.entries(TIME_RANGES).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTimeRange(key)}
                className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  timeRange === key
                    ? 'bg-blue-600 text-white shadow-lg'
                    : `${colors.textSecondary} ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-300'}`
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className={`text-xs sm:text-sm ${colors.textMuted}`}>
          {activeProjects} active project{activeProjects !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className={`grid grid-cols-2 ${hasFeeProjects ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-3 sm:gap-4`}>
        {/* Total Volume Card */}
        <div className={`rounded-xl p-3 sm:p-5 relative overflow-hidden border ${isDark ? 'bg-gradient-to-br from-blue-600/20 to-blue-800/20 border-blue-500/30' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'}`}>
          <div className={`absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 rounded-full -mr-8 sm:-mr-10 -mt-8 sm:-mt-10 ${isDark ? 'bg-blue-500/10' : 'bg-blue-200/50'}`}></div>
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium mb-1 ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>Total Volume</p>
              <p className={`text-xl sm:text-3xl font-bold truncate ${colors.textPrimary}`}>${formatNumber(totalVolume)}</p>
            </div>
            <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${isDark ? 'bg-blue-500/20' : 'bg-blue-200'}`}>
              <TrendingUp className={isDark ? 'text-blue-400' : 'text-blue-600'} size={20} />
            </div>
          </div>
          <div className="mt-2 sm:mt-3 flex items-center gap-1 text-xs sm:text-sm">
            <ArrowUpRight size={14} className="text-green-400" />
            <span className="text-green-400">Active</span>
          </div>
        </div>

        {/* Total Costs Card */}
        <div className={`rounded-xl p-3 sm:p-5 relative overflow-hidden border ${isDark ? 'bg-gradient-to-br from-red-600/20 to-red-800/20 border-red-500/30' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'}`}>
          <div className={`absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 rounded-full -mr-8 sm:-mr-10 -mt-8 sm:-mt-10 ${isDark ? 'bg-red-500/10' : 'bg-red-200/50'}`}></div>
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium mb-1 ${isDark ? 'text-red-300' : 'text-red-600'}`}>Total Costs</p>
              <p className={`text-xl sm:text-3xl font-bold truncate ${colors.textPrimary}`}>${formatNumber(totalCosts)}</p>
            </div>
            <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${isDark ? 'bg-red-500/20' : 'bg-red-200'}`}>
              <DollarSign className={isDark ? 'text-red-400' : 'text-red-600'} size={20} />
            </div>
          </div>
          <div className="mt-2 sm:mt-3 flex items-center gap-1 text-xs sm:text-sm">
            <ArrowDownRight size={14} className="text-red-400" />
            <span className={colors.textSecondary}>Gas + Fees</span>
          </div>
        </div>

        {/* Total Points Card */}
        <div className={`rounded-xl p-3 sm:p-5 relative overflow-hidden border ${isDark ? 'bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 border-yellow-500/30' : 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200'}`}>
          <div className={`absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 rounded-full -mr-8 sm:-mr-10 -mt-8 sm:-mt-10 ${isDark ? 'bg-yellow-500/10' : 'bg-yellow-200/50'}`}></div>
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium mb-1 ${isDark ? 'text-yellow-300' : 'text-yellow-600'}`}>Total Points</p>
              <p className={`text-xl sm:text-3xl font-bold truncate ${colors.textPrimary}`}>{formatNumber(totalPoints)}</p>
            </div>
            <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-200'}`}>
              <Zap className={isDark ? 'text-yellow-400' : 'text-yellow-600'} size={20} />
            </div>
          </div>
          <div className="mt-2 sm:mt-3 flex items-center gap-1 text-xs sm:text-sm">
            <Activity size={14} className="text-yellow-400" />
            <span className={colors.textSecondary}>Accumulated</span>
          </div>
        </div>

        {/* Total Fees Card - Only show if any project uses fees */}
        {hasFeeProjects && (
          <div className={`rounded-xl p-3 sm:p-5 relative overflow-hidden border ${isDark ? 'bg-gradient-to-br from-purple-600/20 to-purple-800/20 border-purple-500/30' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'}`}>
            <div className={`absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 rounded-full -mr-8 sm:-mr-10 -mt-8 sm:-mt-10 ${isDark ? 'bg-purple-500/10' : 'bg-purple-200/50'}`}></div>
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className={`text-xs sm:text-sm font-medium mb-1 ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>Total Fees</p>
                <p className={`text-xl sm:text-3xl font-bold truncate ${colors.textPrimary}`}>${formatNumber(totalFees)}</p>
              </div>
              <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${isDark ? 'bg-purple-500/20' : 'bg-purple-200'}`}>
                <Wallet className={isDark ? 'text-purple-400' : 'text-purple-600'} size={20} />
              </div>
            </div>
            <div className="mt-2 sm:mt-3 flex items-center gap-1 text-xs sm:text-sm">
              <ArrowUpRight size={14} className="text-purple-400" />
              <span className={colors.textSecondary}>Earned</span>
            </div>
          </div>
        )}

        {/* Cost per Point Card */}
        <div className={`rounded-xl p-3 sm:p-5 relative overflow-hidden border ${isDark ? 'bg-gradient-to-br from-green-600/20 to-green-800/20 border-green-500/30' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'}`}>
          <div className={`absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 rounded-full -mr-8 sm:-mr-10 -mt-8 sm:-mt-10 ${isDark ? 'bg-green-500/10' : 'bg-green-200/50'}`}></div>
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium mb-1 ${isDark ? 'text-green-300' : 'text-green-600'}`}>Cost/Point</p>
              <p className={`text-xl sm:text-3xl font-bold truncate ${colors.textPrimary}`}>${costPerPoint.toFixed(3)}</p>
            </div>
            <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${isDark ? 'bg-green-500/20' : 'bg-green-200'}`}>
              <Target className={isDark ? 'text-green-400' : 'text-green-600'} size={20} />
            </div>
          </div>
          <div className="mt-2 sm:mt-3 flex items-center gap-1 text-xs sm:text-sm">
            <Wallet size={14} className="text-green-400" />
            <span className={colors.textSecondary}>Efficiency</span>
          </div>
        </div>
      </div>

      {/* ROI Summary */}
      {(roiCalculation?.base > 0 || roiCalculation?.bullish > 0) && (
        <div className={`rounded-xl p-4 sm:p-6 border ${isDark ? 'bg-gradient-to-r from-purple-900/30 via-slate-800/50 to-blue-900/30 border-purple-500/20' : 'bg-gradient-to-r from-purple-50 via-white to-blue-50 border-purple-200'}`}>
          <h3 className={`text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2 ${colors.textPrimary}`}>
            <TrendingUp className="text-purple-400" size={20} />
            ROI Projections
          </h3>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div className="text-center">
              <p className={`text-xs font-medium mb-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>🔴 Bearish</p>
              <p className={`text-sm sm:text-xl font-bold ${colors.textPrimary}`}>${formatNumber(roiCalculation?.bearish || 0)}</p>
            </div>
            <div className={`text-center border-x ${isDark ? 'border-slate-700' : 'border-gray-200'} px-2 sm:px-4`}>
              <p className={`text-xs font-medium mb-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>🟡 Base</p>
              <p className={`text-base sm:text-2xl font-bold ${colors.textPrimary}`}>${formatNumber(roiCalculation?.base || 0)}</p>
            </div>
            <div className="text-center">
              <p className={`text-xs font-medium mb-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>🟢 Bullish</p>
              <p className={`text-sm sm:text-xl font-bold ${colors.textPrimary}`}>${formatNumber(roiCalculation?.bullish || 0)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Projects Progress */}
      {hasData && projectsWithProgress > 0 ? (
        <div className={`${colors.bgCard} border ${colors.border} rounded-xl p-4 sm:p-6 ${colors.cardShadow}`}>
          <h3 className={`text-lg sm:text-xl font-bold mb-3 sm:mb-4 flex items-center gap-2 ${colors.textPrimary}`}>
            <Activity className="text-blue-400" size={20} />
            Weekly Progress
          </h3>
          <div className="space-y-3 sm:space-y-4">
            {Object.entries(weeklyProgress).map(([project, data]) => {
              const settings = projectSettings[project] || {};
              const metricType = settings.metricType || 'points';
              const target = settings.weeklyTarget || 0;
              
              let current = 0;
              let metricLabel = 'pts';
              
              switch (metricType) {
                case 'points':
                  current = data?.points || 0;
                  metricLabel = 'pts';
                  break;
                case 'volume':
                  current = data?.volume || 0;
                  metricLabel = 'vol';
                  break;
                case 'fees':
                  current = data?.fees || 0;
                  metricLabel = 'fees';
                  break;
                case 'custom':
                  current = data?.customMetric || data?.points || 0;
                  metricLabel = settings.customMetricName || 'units';
                  break;
              }
              
              const percentage = target > 0 ? Math.min((current / target) * 100, 100) : 0;
              const isComplete = percentage >= 100;

              return (
                <div key={project} className={`rounded-lg p-3 sm:p-4 ${isDark ? 'bg-slate-700/30' : 'bg-gray-100'}`}>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-2 sm:mb-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                      <span className={`font-medium text-sm sm:text-base ${colors.textPrimary}`}>{project}</span>
                      {isComplete && (
                        <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full font-medium">
                          ✓ Complete
                        </span>
                      )}
                    </div>
                    <div className="text-right ml-4 sm:ml-0">
                      <span className={`font-bold text-sm sm:text-base ${colors.textPrimary}`}>{formatNumber(current)}</span>
                      {target > 0 && <span className={`text-xs sm:text-base ${colors.textMuted}`}> / {formatNumber(target)}</span>}
                      <span className={`text-xs sm:text-sm ml-1 ${colors.textSecondary}`}>
                        {metricLabel}
                      </span>
                    </div>
                  </div>
                  {target > 0 && (
                    <>
                      <div className={`relative h-2 sm:h-3 rounded-full overflow-hidden ${isDark ? 'bg-slate-600' : 'bg-gray-300'}`}>
                        <div
                          className={`absolute h-full transition-all duration-500 rounded-full ${
                            isComplete 
                              ? 'bg-gradient-to-r from-green-500 to-green-400' 
                              : 'bg-gradient-to-r from-blue-600 to-blue-400'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <div className="mt-1 sm:mt-2 text-right">
                        <span className={`text-xs sm:text-sm font-medium ${isComplete ? 'text-green-400' : colors.textSecondary}`}>
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className={`${colors.bgCard} border ${colors.border} rounded-xl p-8 sm:p-12 text-center ${colors.cardShadow}`}>
          <div className={`w-16 sm:w-20 h-16 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-700/50' : 'bg-gray-200'}`}>
            <Target size={32} className={colors.textMuted} />
          </div>
          <h3 className={`text-xl sm:text-2xl font-bold mb-2 sm:mb-3 ${colors.textPrimary}`}>Welcome to FarmDash!</h3>
          <p className={`max-w-md mx-auto mb-4 sm:mb-6 text-sm sm:text-base ${colors.textSecondary}`}>
            Your dashboard is ready. Start by adding your first airdrops and targets to track your farming progress.
          </p>
          <div className="flex justify-center gap-2 sm:gap-4">
            <div className={`rounded-lg p-3 sm:p-4 text-center ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <div className="text-xl sm:text-2xl mb-1">📋</div>
              <p className={`text-xs sm:text-sm ${colors.textSecondary}`}>Add Airdrops</p>
            </div>
            <div className={`rounded-lg p-3 sm:p-4 text-center ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <div className="text-xl sm:text-2xl mb-1">🎯</div>
              <p className={`text-xs sm:text-sm ${colors.textSecondary}`}>Set Targets</p>
            </div>
            <div className={`rounded-lg p-3 sm:p-4 text-center ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
              <div className="text-xl sm:text-2xl mb-1">📊</div>
              <p className={`text-xs sm:text-sm ${colors.textSecondary}`}>Track Progress</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};